/** Keeps the tab title, canonical link and robots tag in step with the current screen (the server sets the same for first loads). */
import { useEffect } from 'react';
import { useLocation } from 'react-router';

const GAME_NAMES: Record<string, string> = { CALL_BREAK: 'Call Break', SPADES: 'Spades', TARNEEB: 'Tarneeb', HEARTS: 'Hearts', FIVE_HUNDRED: 'Five Hundred', JUDGEMENT: 'Judgement', RUMMY: 'Rummy', EUCHRE: 'Euchre', GIN_RUMMY: 'Gin Rummy', TWENTY_NINE: 'Twenty-Nine', CRAZY_EIGHTS: 'Crazy Eights', CRIBBAGE: 'Cribbage', BLACKJACK: 'Blackjack', BHABHI: 'Bhabhi', PINOCHLE: 'Pinochle', ANDAR_BAHAR: 'Andar Bahar', BRIDGE: 'Bridge', SEVENS: 'Sevens', SCOPA: 'Scopa', BACCARAT: 'Baccarat', BLUFF: 'Bluff', DRAGON_TIGER: 'Dragon Tiger', FLUSH: 'Flush', COURT_PIECE: 'Court Piece', DEHLA_PAKAD: 'Dehla Pakad', WHIST: 'Whist', SEEP: 'Seep', BELOTE: 'Belote', DURAK: 'Durak', PRESIDENT: 'President', BRISCOLA: 'Briscola', CASINO_WAR: 'Casino War', THREE_CARD_POKER: 'Three Card Poker', GO_FISH: 'Go Fish', TRESSETTE: 'Tressette', RED_DOG: 'Red Dog', CANASTA: 'Canasta', SKAT: 'Skat', PAI_GOW: 'Pai Gow Poker', BIG_TWO: 'Big Two', WAR: 'War', RUMMY_500: 'Rummy 500', BRAG: 'Three Card Brag', PITCH: 'Pitch', HAZARI: 'Hazari', TEEN_PATTI: 'Teen Patti', POKER: 'Poker', MARRIAGE: 'Marriage' };
const HOME_TITLE = 'Card Hub — Play Call Break, Teen Patti, Poker & Marriage Online';

export interface PageSeo {
  title: string;
  canonical: string;
  index: boolean;
}

export function seoFor(pathname: string): PageSeo {
  const tutorial = /^\/tutorial\/([A-Z_]+)\/?$/.exec(pathname);
  if (tutorial?.[1] && GAME_NAMES[tutorial[1]]) return { title: `How to play ${GAME_NAMES[tutorial[1]]} — rules & tutorial | Card Hub`, canonical: `/tutorial/${tutorial[1]}`, index: true };
  if (pathname === '/help') return { title: 'Help & rules — Card Hub', canonical: '/help', index: true };
  if (pathname === '/' || pathname === '/welcome') return { title: HOME_TITLE, canonical: '/', index: true };
  const games = /^\/games\/([A-Z_]+)\/?$/.exec(pathname);
  if (games?.[1] && GAME_NAMES[games[1]]) return { title: `${GAME_NAMES[games[1]]} tables | Card Hub`, canonical: '/', index: false };
  return { title: 'Card Hub', canonical: '/', index: false };
}

function setMeta(selector: string, make: () => HTMLElement, attr: string, value: string) {
  let el = document.head.querySelector<HTMLElement>(selector);
  if (!el) {
    el = make();
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

/** The app is one page as far as the browser is concerned, so tell Google Analytics about each screen change. */
function trackPageView(path: string, title: string): void {
  const h = window.location.hostname;
  if (h === 'localhost' || h === '127.0.0.1' || typeof window.gtag !== 'function') return;
  window.gtag('event', 'page_view', { page_path: path, page_location: window.location.href, page_title: title });
}

export function useSeo(): void {
  const { pathname } = useLocation();
  useEffect(() => {
    const seo = seoFor(pathname);
    document.title = seo.title;
    trackPageView(pathname, seo.title);
    setMeta('meta[name="robots"]', () => Object.assign(document.createElement('meta'), { name: 'robots' }), 'content', seo.index ? 'index, follow, max-image-preview:large' : 'noindex, nofollow');
    setMeta('link[rel="canonical"]', () => Object.assign(document.createElement('link'), { rel: 'canonical' }), 'href', window.location.origin + (seo.canonical === '/' ? '/' : seo.canonical));
  }, [pathname]);
}
